'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { Menu, X, Sparkles } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const navLinks = [
  { label: 'For Business', href: '#business' },
  { label: 'For Personal', href: '#personal' },
  { label: 'How It Works', href: '#how-it-works' },
  { label: 'Pricing', href: '/pricing' },
  { label: 'Examples', href: '/examples' },
];

export default function HomepageNav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, loading } = useAuth();

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-surface-1 border-b border-border shadow-lg shadow-black/20'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16">
          <div className="flex items-center justify-between h-16">
            <Link href="/" className="flex items-center gap-2.5 group">
              <AppLogo size={32} />
              <span className="font-sans font-bold text-lg tracking-tight text-foreground group-hover:text-primary transition-colors">
                DESIGNHUB <span className="text-gradient-gold">AI</span>
              </span>
            </Link>

            <div className="hidden lg:flex items-center gap-1">
              {navLinks?.map((link) => (
                link?.href?.startsWith('#') ? (
                  <a
                    key={`nav-${link?.label}`}
                    href={link?.href}
                    className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-white/5"
                  >
                    {link?.label}
                  </a>
                ) : (
                  <Link
                    key={`nav-${link?.label}`}
                    href={link?.href}
                    className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-white/5"
                  >
                    {link?.label}
                  </Link>
                )
              ))}
            </div>

            <div className="hidden lg:flex items-center gap-3">
              {!loading && (
                user ? (
                  <Link
                    href="/dashboard"
                    className="btn-primary flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold"
                  >
                    <Sparkles size={15} />
                    Dashboard
                  </Link>
                ) : (
                  <>
                    <Link
                      href="/auth/sign-in"
                      className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                    >
                      Sign In
                    </Link>
                    <Link
                      href="/auth/sign-up"
                      className="btn-primary flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold"
                    >
                      <Sparkles size={15} />
                      Start Creating
                    </Link>
                  </>
                )
              )}
            </div>

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors"
              aria-label="Toggle mobile menu"
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </nav>
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          <div className="absolute top-0 right-0 bottom-0 w-72 bg-card border-l border-border flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <span className="font-bold text-lg">
                DESIGNHUB <span className="text-gradient-gold">AI</span>
              </span>
              <button
                onClick={() => setMobileOpen(false)}
                className="p-2 rounded-lg hover:bg-white/5 text-muted-foreground"
                aria-label="Close menu"
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 p-4 space-y-1">
              {navLinks?.map((link) => (
                link?.href?.startsWith('#') ? (
                  <a
                    key={`mobile-nav-${link?.label}`}
                    href={link?.href}
                    onClick={() => setMobileOpen(false)}
                    className="block px-4 py-3 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-white/5 rounded-lg transition-colors"
                  >
                    {link?.label}
                  </a>
                ) : (
                  <Link
                    key={`mobile-nav-${link?.label}`}
                    href={link?.href}
                    onClick={() => setMobileOpen(false)}
                    className="block px-4 py-3 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-white/5 rounded-lg transition-colors"
                  >
                    {link?.label}
                  </Link>
                )
              ))}
            </div>
            <div className="p-4 border-t border-border space-y-2">
              {!loading && (
                user ? (
                  <Link
                    href="/dashboard"
                    className="btn-primary flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-xl text-sm font-semibold"
                    onClick={() => setMobileOpen(false)}
                  >
                    <Sparkles size={15} />
                    Dashboard
                  </Link>
                ) : (
                  <>
                    <Link
                      href="/auth/sign-in"
                      className="block w-full text-center px-4 py-2.5 text-sm font-medium btn-secondary rounded-xl"
                      onClick={() => setMobileOpen(false)}
                    >
                      Sign In
                    </Link>
                    <Link
                      href="/auth/sign-up"
                      className="btn-primary flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-xl text-sm font-semibold"
                      onClick={() => setMobileOpen(false)}
                    >
                      <Sparkles size={15} />
                      Start Creating Free
                    </Link>
                  </>
                )
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}